import React, { useState, useEffect } from 'react';

// Color palette
const colors = {
  primary: '#2E75B6',
  primaryDark: '#1F4E79',
  primaryLight: '#E8F1F8',
  accent: '#5B8A6B',
  accentLight: '#EDF3EE',
  bg: '#FFFFFF',
  bgAlt: '#F7F9FB',
  text: '#1F2937',
  textMuted: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
  success: '#10B981',
  successBg: '#D1FAE5'
};

// Logo component
const Logo = ({ size = 'normal' }) => {
  const scale = size === 'large' ? 1.3 : 1;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 64" width={260 * scale} height={64 * scale}>
      <g transform="translate(4, 6)">
        <path d="M 28 4 L 4 22 L 4 52 L 52 52 L 52 22 Z" fill="none" stroke={colors.primary} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round"/>
        <path d="M 28 42 C 28 42, 18 36, 18 28 C 18 24, 21 22, 24 22 C 26 22, 27.5 23, 28 24 C 28.5 23, 30 22, 32 22 C 35 22, 38 24, 38 28 C 38 36, 28 42, 28 42 Z" fill={colors.accent}/>
      </g>
      <text x="72" y="42" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif" fontSize="30" fontWeight="700" fill={colors.primaryDark}>Taylor Care</text>
    </svg>
  );
};

// SVG illustrations for service cards
const ServiceIllustration = ({ type }) => {
  const illustrations = {
    personal: (
      <svg viewBox="0 0 80 80" width="64" height="64">
        <circle cx="40" cy="26" r="10" fill={colors.primaryLight} stroke={colors.primary} strokeWidth="2"/>
        <path d="M 22 60 Q 22 44 40 44 Q 58 44 58 60" fill={colors.primaryLight} stroke={colors.primary} strokeWidth="2" strokeLinecap="round"/>
        <path d="M 32 38 L 28 50 M 48 38 L 52 50" stroke={colors.accent} strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    companion: (
      <svg viewBox="0 0 80 80" width="64" height="64">
        <circle cx="28" cy="30" r="8" fill={colors.primaryLight} stroke={colors.primary} strokeWidth="2"/>
        <circle cx="52" cy="30" r="8" fill={colors.accentLight} stroke={colors.accent} strokeWidth="2"/>
        <path d="M 16 60 Q 16 46 28 46 Q 40 46 40 60" fill={colors.primaryLight} stroke={colors.primary} strokeWidth="2"/>
        <path d="M 40 60 Q 40 46 52 46 Q 64 46 64 60" fill={colors.accentLight} stroke={colors.accent} strokeWidth="2"/>
      </svg>
    ),
    home: (
      <svg viewBox="0 0 80 80" width="64" height="64">
        <path d="M 40 14 L 16 32 L 16 62 L 64 62 L 64 32 Z" fill={colors.primaryLight} stroke={colors.primary} strokeWidth="2" strokeLinejoin="round"/>
        <rect x="34" y="44" width="12" height="18" fill={colors.primary}/>
        <rect x="22" y="38" width="8" height="8" fill={colors.accent}/>
        <rect x="50" y="38" width="8" height="8" fill={colors.accent}/>
      </svg>
    ),
    medication: (
      <svg viewBox="0 0 80 80" width="64" height="64">
        <rect x="20" y="24" width="40" height="32" rx="6" fill={colors.primaryLight} stroke={colors.primary} strokeWidth="2"/>
        <line x1="40" y1="24" x2="40" y2="56" stroke={colors.primary} strokeWidth="2"/>
        <circle cx="30" cy="40" r="3" fill={colors.accent}/>
        <circle cx="50" cy="40" r="3" fill={colors.accent}/>
        <path d="M 28 16 L 28 22 M 52 16 L 52 22" stroke={colors.primary} strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    schedule: (
      <svg viewBox="0 0 80 80" width="64" height="64">
        <circle cx="40" cy="40" r="24" fill={colors.primaryLight} stroke={colors.primary} strokeWidth="2"/>
        <line x1="40" y1="40" x2="40" y2="24" stroke={colors.primaryDark} strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="40" y1="40" x2="52" y2="40" stroke={colors.accent} strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="40" cy="40" r="2.5" fill={colors.primaryDark}/>
      </svg>
    ),
    shield: (
      <svg viewBox="0 0 80 80" width="64" height="64">
        <path d="M 40 12 L 18 22 L 18 42 Q 18 58 40 68 Q 62 58 62 42 L 62 22 Z" fill={colors.accentLight} stroke={colors.accent} strokeWidth="2" strokeLinejoin="round"/>
        <path d="M 30 40 L 38 48 L 52 32" fill="none" stroke={colors.accent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  };
  return illustrations[type];
};

export default function TaylorCareWebsite() {
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Update URL hash on scroll
  useEffect(() => {
    const sections = ['home', 'services', 'about', 'pricing', 'contact'];
    const handleScroll = () => {
      const scrollPos = window.scrollY + 100;
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollPos && el.offsetTop + el.offsetHeight > scrollPos) {
          setActiveSection(id);
          if (window.location.hash !== `#${id}`) {
            window.history.replaceState(null, '', `#${id}`);
          }
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setContactForm({ name: '', phone: '', email: '', message: '' });
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'services', label: 'Services' },
    { id: 'about', label: 'About' },
    { id: 'pricing', label: 'Care' },
    { id: 'contact', label: 'Contact' }
  ];

  const services = [
    { type: 'personal', title: 'Personal Care Assistance', description: 'Help with bathing, grooming, dressing, toileting, and other activities of daily living.' },
    { type: 'companion', title: 'Companionship', description: 'Meaningful conversation, social engagement, and emotional support for seniors and adults.' },
    { type: 'home', title: 'Light Housekeeping', description: 'Meal preparation, light cleaning, laundry, and household organization.' },
    { type: 'medication', title: 'Medication Reminders', description: 'Helping clients remember to take prescribed medications at scheduled times.' },
    { type: 'schedule', title: 'Flexible Scheduling', description: 'Part-time, full-time, or short-term care tailored to your specific needs.' },
    { type: 'shield', title: 'Licensed & Insured', description: 'Fully licensed by PA DOH with comprehensive liability and professional insurance.' }
  ];

  const whyChooseUs = [
    { title: 'PA Licensed & Certified', description: 'Fully licensed home care agency meeting all Pennsylvania Department of Health regulations.' },
    { title: 'Background-Checked Staff', description: 'All caregivers pass PA State Police clearance and criminal background screening.' },
    { title: 'Comprehensive Training', description: 'Staff trained in all 16 competency areas required by Pennsylvania law.' },
    { title: 'Medicaid & Private Pay', description: 'We accept both Medicaid funding (HCBS/Aging Waiver) and private pay clients.' },
    { title: 'Responsive Support', description: 'Always available for questions, concerns, and service adjustments.' },
    { title: 'Adult-Focused Care', description: 'Specialized care for adults 18+, with experience in post-hospitalization recovery.' }
  ];

  const styles = {
    container: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: colors.text, lineHeight: '1.6', margin: 0, padding: 0 },
    section: { padding: '5rem 2rem', scrollMarginTop: '80px' },
    sectionInner: { maxWidth: '1100px', margin: '0 auto' },
    h2: { fontSize: '36px', fontWeight: '700', margin: '0 0 1rem 0', color: colors.primaryDark, textAlign: 'center', letterSpacing: '-0.5px' },
    subtitle: { fontSize: '17px', color: colors.textMuted, margin: '0 auto 3.5rem auto', textAlign: 'center', maxWidth: '650px', lineHeight: '1.6' },
    card: { background: colors.bg, padding: '2rem', borderRadius: '14px', border: `1px solid ${colors.border}`, transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    button: { padding: '0.875rem 1.75rem', background: colors.primary, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none', display: 'inline-block', transition: 'all 0.2s' },
    buttonOutline: { padding: '0.875rem 1.75rem', background: 'transparent', color: colors.primary, border: `2px solid ${colors.primary}`, borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none', display: 'inline-block', transition: 'all 0.2s' },
    input: { padding: '0.875rem 1rem', border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', background: colors.bg, outline: 'none' },
    navLink: (isActive) => ({ padding: '0.5rem 1rem', color: isActive ? colors.primary : colors.text, background: 'transparent', border: 'none', fontSize: '15px', fontWeight: isActive ? '600' : '500', cursor: 'pointer', textDecoration: 'none', borderRadius: '6px', transition: 'all 0.2s', fontFamily: 'inherit' })
  };

  return (
    <div style={styles.container}>
      {/* Sticky Header / Navigation */}
      <header style={{ background: colors.bg, borderBottom: `1px solid ${colors.border}`, padding: '0.75rem 2rem', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <button onClick={() => scrollToSection('home')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
            <Logo />
          </button>

          {/* Desktop Nav */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} className="desktop-nav">
            {navLinks.map(link => (
              <button key={link.id} onClick={() => scrollToSection(link.id)} style={styles.navLink(activeSection === link.id)}>
                {link.label}
              </button>
            ))}
            <a href="tel:2158287600" style={{ ...styles.button, marginLeft: '1rem', padding: '0.625rem 1.25rem', fontSize: '14px' }}>
              📞 (267) 682-8500
            </a>
          </nav>

          {/* Mobile menu button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ display: 'none', background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '0.5rem' }} className="mobile-menu-btn" aria-label="Menu">
            ☰
          </button>
        </div>

        {/* Mobile Nav Dropdown */}
        {mobileMenuOpen && (
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
            {navLinks.map(link => (
              <button key={link.id} onClick={() => scrollToSection(link.id)} style={{ ...styles.navLink(activeSection === link.id), textAlign: 'left' }}>
                {link.label}
              </button>
            ))}
            <a href="tel:2158287600" style={{ ...styles.button, marginTop: '0.5rem', textAlign: 'center' }}>📞 (267) 682-8500</a>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" style={{ ...styles.section, background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.bg} 100%)`, paddingTop: '5rem', paddingBottom: '5rem' }}>
        <div style={{ ...styles.sectionInner, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }} className="hero-grid">
          <div>
            <div style={{ display: 'inline-block', padding: '6px 14px', background: colors.bg, color: colors.primary, borderRadius: '999px', fontSize: '13px', fontWeight: '600', marginBottom: '1.5rem', border: `1px solid ${colors.primary}` }}>
              ✓ PA Licensed & Insured
            </div>
            <h2 style={{ fontSize: '44px', fontWeight: '700', margin: '0 0 1.25rem 0', color: colors.primaryDark, letterSpacing: '-1px', lineHeight: '1.15' }}>
              Compassionate home care for adults in the Philadelphia area
            </h2>
            <p style={{ fontSize: '18px', color: colors.textMuted, margin: '0 0 2rem 0', lineHeight: '1.7' }}>
              Taylor Care LLC provides professional, dignified in-home support for adults 18 and older. Fully licensed, fully insured, and committed to helping your loved ones thrive at home.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button onClick={() => scrollToSection('contact')} style={styles.button}>Get a Free Consultation</button>
              <a href="tel:2158287600" style={styles.buttonOutline}>Call (267) 682-8500</a>
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', marginTop: '2.5rem', flexWrap: 'wrap' }}>
              <div><div style={{ fontSize: '32px', fontWeight: '700', color: colors.primaryDark, lineHeight: 1 }}>4</div><div style={{ fontSize: '13px', color: colors.textMuted, marginTop: '4px' }}>Counties served</div></div>
              <div><div style={{ fontSize: '32px', fontWeight: '700', color: colors.primaryDark, lineHeight: 1 }}>18+</div><div style={{ fontSize: '13px', color: colors.textMuted, marginTop: '4px' }}>Adult-focused care</div></div>
              <div><div style={{ fontSize: '32px', fontWeight: '700', color: colors.primaryDark, lineHeight: 1 }}>24/7</div><div style={{ fontSize: '13px', color: colors.textMuted, marginTop: '4px' }}>Flexible scheduling</div></div>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <img
              src="/taylor-care-hero-photo.png"
              alt="Taylor Care caregiver assisting senior client with a meal at home"
              style={{ width: '100%', height: 'auto', borderRadius: '16px', boxShadow: '0 10px 40px rgba(31, 78, 121, 0.15)', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" style={{ ...styles.section, background: colors.bg }}>
        <div style={styles.sectionInner}>
          <h2 style={styles.h2}>Our Services</h2>
          <p style={styles.subtitle}>Comprehensive in-home care tailored to your loved one's unique needs and preferences</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {services.map((service, idx) => (
              <div key={idx} style={styles.card}>
                <div style={{ marginBottom: '1.25rem' }}>
                  <ServiceIllustration type={service.type} />
                </div>
                <h3 style={{ fontSize: '19px', fontWeight: '600', margin: '0 0 0.5rem 0', color: colors.primaryDark }}>{service.title}</h3>
                <p style={{ fontSize: '15px', color: colors.textMuted, margin: 0, lineHeight: '1.6' }}>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Why Choose Us */}
      <section id="about" style={{ ...styles.section, background: colors.bgAlt }}>
        <div style={styles.sectionInner}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center', marginBottom: '4rem' }} className="hero-grid">
            <div>
              <img
                src="https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800&q=80"
                alt="Caring caregiver helping senior with kindness"
                style={{ width: '100%', height: 'auto', borderRadius: '16px', boxShadow: '0 10px 40px rgba(31, 78, 121, 0.12)', display: 'block' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div>
              <p style={{ fontSize: '13px', color: colors.primary, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 1rem 0' }}>About Taylor Care</p>
              <h2 style={{ ...styles.h2, textAlign: 'left', margin: '0 0 1.25rem 0' }}>A higher standard of in-home care</h2>
              <p style={{ fontSize: '16px', color: colors.textMuted, lineHeight: '1.7', margin: '0 0 1rem 0' }}>
                Taylor Care LLC was founded with a simple belief: every adult deserves to age and recover with dignity, in the comfort of their own home. We provide personalized, professional care that respects each client's independence and preferences.
              </p>
              <p style={{ fontSize: '16px', color: colors.textMuted, lineHeight: '1.7', margin: 0 }}>
                As a Pennsylvania-licensed home care agency, we meet rigorous state standards for caregiver training, background screening, and quality of service. Our team is here to provide the support your family needs.
              </p>
            </div>
          </div>

          <h3 style={{ fontSize: '24px', fontWeight: '700', color: colors.primaryDark, textAlign: 'center', margin: '0 0 2.5rem 0' }}>Why families choose us</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {whyChooseUs.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', background: colors.bg, padding: '1.5rem', borderRadius: '12px', border: `1px solid ${colors.border}` }}>
                <div style={{ flexShrink: 0, width: '44px', height: '44px', background: colors.accentLight, color: colors.accent, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700' }}>✓</div>
                <div>
                  <h4 style={{ fontSize: '17px', fontWeight: '600', margin: '0 0 0.4rem 0', color: colors.primaryDark }}>{item.title}</h4>
                  <p style={{ fontSize: '14px', color: colors.textMuted, margin: 0, lineHeight: '1.6' }}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section style={{ ...styles.section, background: colors.bg }}>
        <div style={styles.sectionInner}>
          <h2 style={styles.h2}>Service Area</h2>
          <p style={styles.subtitle}>We proudly serve adults throughout the greater Philadelphia area</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', maxWidth: '900px', margin: '0 auto' }}>
            {['Philadelphia', 'Montgomery County', 'Bucks County', 'Delaware County'].map((area) => (
              <div key={area} style={{ padding: '1.75rem 1rem', background: colors.primaryLight, borderRadius: '12px', textAlign: 'center', border: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: '28px', marginBottom: '0.5rem' }}>📍</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: colors.primaryDark }}>{area}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section style={{ ...styles.section, background: colors.bgAlt }}>
        <div style={styles.sectionInner}>
          <h2 style={styles.h2}>Credentials & Compliance</h2>
          <p style={styles.subtitle}>Fully licensed, fully insured, committed to the highest standards of care</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              { label: 'PA Department of Health', title: 'Licensed Home Care Agency', sub: 'Division of Home Health, License # [Your License #]' },
              { label: 'Insurance Coverage', title: 'Fully Insured', sub: 'General Liability, Professional Liability, Workers Compensation' },
              { label: 'Staff Screening', title: 'Background Checked', sub: 'PA State Police clearance and TB screening required for all caregivers' }
            ].map((item, idx) => (
              <div key={idx} style={styles.card}>
                <p style={{ fontSize: '12px', color: colors.primary, margin: '0 0 0.5rem 0', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</p>
                <p style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 0.5rem 0', color: colors.primaryDark }}>{item.title}</p>
                <p style={{ fontSize: '14px', color: colors.textMuted, margin: 0, lineHeight: '1.5' }}>{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Care Options */}
      <section id="pricing" style={{ ...styles.section, background: colors.bg }}>
        <div style={styles.sectionInner}>
          <h2 style={styles.h2}>Care Options</h2>
          <p style={styles.subtitle}>Every family's situation is different. We tailor a care plan to your loved one's specific needs and budget — and offer a free in-home consultation to walk you through your options.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
            {[
              { title: 'Personal Care Services', description: 'Bathing, grooming, dressing, toileting, and other activities of daily living — customized to your loved one\'s needs.' },
              { title: 'Companionship & Wellness', description: 'Social engagement, conversation, medication reminders, and routine wellness check-ins. Flexible hourly or daily care.' },
              { title: 'Specialized Care', description: 'Post-hospitalization recovery, dementia support, and complex care needs requiring extra training and attention.' },
              { title: 'Medicaid-Funded Services', description: 'If you qualify for the HCBS Waiver, Aging Waiver, or Act 150 Program, services may be covered at little to no cost. We assist with eligibility.' }
            ].map((item) => (
              <div key={item.title} style={{ ...styles.card, padding: '2rem' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 0.75rem 0', color: colors.primaryDark }}>{item.title}</h3>
                <p style={{ fontSize: '14px', color: colors.textMuted, margin: '0 0 1.25rem 0', lineHeight: '1.6' }}>{item.description}</p>
                <p style={{ fontSize: '13px', color: colors.accent, margin: 0, fontWeight: '600' }}>
                  ✓ Free in-home consultation
                </p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <p style={{ fontSize: '15px', color: colors.textMuted, margin: '0 0 1.5rem 0' }}>
              Not sure what level of care is right? Let's talk. No commitment, no pressure.
            </p>
            <button onClick={() => scrollToSection('contact')} style={styles.button}>
              Schedule a Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" style={{ ...styles.section, background: colors.bgAlt }}>
        <div style={{ maxWidth: '650px', margin: '0 auto' }}>
          <h2 style={styles.h2}>Get in Touch</h2>
          <p style={styles.subtitle}>Have questions? Send us a message and we'll respond within 24 hours.</p>

          {submitted && (
            <div style={{ background: colors.successBg, border: `1px solid ${colors.success}`, padding: '1rem 1.25rem', borderRadius: '8px', marginBottom: '1.5rem', color: colors.success, fontSize: '15px', fontWeight: '500' }}>
              ✓ Thank you! We'll contact you within 24 hours.
            </div>
          )}

          <form onSubmit={handleContactSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', background: colors.bg, padding: '2rem', borderRadius: '14px', border: `1px solid ${colors.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <input type="text" placeholder="Your name *" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} style={styles.input} />
            <input type="tel" placeholder="Phone number *" required value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} style={styles.input} />
            <input type="email" placeholder="Email (optional)" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} style={styles.input} />
            <textarea placeholder="Tell us about your care needs..." required value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} style={{ ...styles.input, minHeight: '120px', fontFamily: 'inherit', resize: 'vertical' }} />
            <button type="submit" style={{ ...styles.button, fontSize: '16px', padding: '1rem' }}>Send Message</button>
          </form>

          <div style={{ marginTop: '2.5rem', padding: '2rem', background: colors.bg, borderRadius: '14px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
            <p style={{ fontSize: '13px', fontWeight: '700', margin: '0 0 1rem 0', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '1px' }}>Or contact us directly</p>
            <p style={{ fontSize: '24px', fontWeight: '700', margin: '0 0 0.5rem 0', color: colors.primary }}>📞 (267) 682-8500</p>
            <p style={{ fontSize: '15px', margin: '0 0 0.25rem 0', color: colors.textMuted }}>✉ info@taylorcarepa.com</p>
            <p style={{ fontSize: '14px', margin: '0.75rem 0 0 0', color: colors.textLight }}>📍 1026 Lifford Rd, Cheltenham, PA 19012</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '3rem 2rem 2rem 2rem', background: colors.primaryDark, color: '#fff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
          <div>
            <p style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 0.5rem 0' }}>Taylor Care LLC</p>
            <p style={{ fontSize: '14px', opacity: 0.85, margin: '0 0 0.5rem 0' }}>Licensed Home Care Agency</p>
            <p style={{ fontSize: '13px', opacity: 0.7, margin: 0 }}>Cheltenham, PA</p>
          </div>
          <div>
            <p style={{ fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 1rem 0', opacity: 0.85 }}>Quick Links</p>
            {navLinks.map(link => (
              <button key={link.id} onClick={() => scrollToSection(link.id)} style={{ display: 'block', background: 'transparent', border: 'none', color: '#fff', fontSize: '14px', opacity: 0.85, cursor: 'pointer', padding: '0.25rem 0', textAlign: 'left', fontFamily: 'inherit' }}>
                {link.label}
              </button>
            ))}
          </div>
          <div>
            <p style={{ fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 1rem 0', opacity: 0.85 }}>Contact</p>
            <p style={{ fontSize: '14px', margin: '0 0 0.5rem 0', opacity: 0.85 }}>📞 (267) 682-8500</p>
            <p style={{ fontSize: '14px', margin: '0 0 0.5rem 0', opacity: 0.85 }}>✉ info@taylorcarepa.com</p>
            <p style={{ fontSize: '14px', margin: 0, opacity: 0.85 }}>📍 1026 Lifford Rd<br/>Cheltenham, PA 19012</p>
          </div>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '12px', opacity: 0.6 }}>© 2026 Taylor Care LLC. All rights reserved. Fully licensed and insured.</p>
        </div>
      </footer>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
          .hero-grid { grid-template-columns: 1fr !important; }
        }
        button:hover, a:hover { opacity: 0.9; }
        input:focus, textarea:focus { border-color: ${colors.primary} !important; box-shadow: 0 0 0 3px ${colors.primaryLight} !important; }
      `}</style>
    </div>
  );
}
